import { useTranslation } from "react-i18next";
import { Home, ChevronRight } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useTruncated } from "@/lib/useTruncated";
import { cn } from "@/lib/utils";

/**
 * Drill-down breadcrumb (FN / ADR-014): 🏠 > Ancestor > … > Current. `ancestors` is the chain from the nearest root
 * down to (but excluding) the current node, derived from the ltree `path`; any ancestor missing from the permitted set
 * is passed as `null` and renders as a non-clickable placeholder ("…") — never a dead link. Home + each ancestor are
 * clickable (drill there); the current node is `aria-current` and not a link. Segments truncate at a max-width and,
 * WHEN truncated, reveal the full name in a {@link Tooltip} on hover/focus (never the native `title`, which is slow).
 */
export function FacilityBreadcrumb({
  ancestors,
  current,
  onHome,
  onCrumb,
}: {
  ancestors: readonly (FacilityNode | null)[];
  current: FacilityNode;
  onHome: () => void;
  onCrumb: (id: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("facilitySelection.nav.breadcrumb")}>
      <ol className="text-muted-foreground flex flex-wrap items-center gap-1 text-sm">
        <li>
          <button
            type="button"
            onClick={onHome}
            aria-label={t("facilitySelection.chip.all")}
            className="hover:text-foreground focus-visible:ring-ring/50 inline-flex items-center rounded-sm p-0.5 outline-none focus-visible:ring-2"
          >
            <Home aria-hidden="true" className="size-4" />
          </button>
        </li>
        {ancestors.map((a, i) => (
          <li key={a ? a.id : `gap-${i}`} className="flex items-center gap-1">
            <ChevronRight aria-hidden="true" className="size-3.5 opacity-60" />
            {a ? (
              <AncestorCrumb name={a.name} onSelect={() => onCrumb(a.id)} />
            ) : (
              <span className="px-0.5" aria-hidden="true">
                …
              </span>
            )}
          </li>
        ))}
        <li className="flex items-center gap-1">
          <ChevronRight aria-hidden="true" className="size-3.5 opacity-60" />
          <CurrentCrumb name={current.name} />
        </li>
      </ol>
    </nav>
  );
}

/** A clickable ancestor segment — truncates, tooltip only when truncated. */
function AncestorCrumb({ name, onSelect }: { name: string; onSelect: () => void }) {
  const { ref, isTruncated, measure } = useTruncated<HTMLButtonElement>(name);
  return (
    <TooltipTrigger delay={0} isDisabled={!isTruncated}>
      <button
        ref={ref}
        type="button"
        onClick={onSelect}
        onPointerEnter={measure}
        className="hover:text-foreground focus-visible:ring-ring/50 max-w-[12rem] truncate rounded-sm px-0.5 outline-none focus-visible:ring-2"
      >
        {name}
      </button>
      <Tooltip>{name}</Tooltip>
    </TooltipTrigger>
  );
}

/** The current (non-link) segment — truncates, tooltip only when truncated; out of the tab order. */
function CurrentCrumb({ name }: { name: string }) {
  const { ref, isTruncated, measure } = useTruncated<HTMLSpanElement>(name);
  return (
    <TooltipTrigger delay={0} isDisabled={!isTruncated} excludeFromTabOrder>
      <span
        ref={ref}
        aria-current="page"
        onPointerEnter={measure}
        className={cn("text-foreground max-w-[14rem] truncate px-0.5 font-medium")}
      >
        {name}
      </span>
      <Tooltip>{name}</Tooltip>
    </TooltipTrigger>
  );
}
